import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReplayIcon from '@mui/icons-material/Replay';
import SelectorAreaAtencion from './SelectorAreaAtencion';
import CamposTramiteExpres from './CamposTramiteExpres';
import { registrarReingreso } from '../../services/turneroService';
import { derivarAtencion } from '../../constants/areasAtencion';
import { formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { DATOS_EXPRES_VACIOS, datosExpresValidos } from '../../constants/tramiteExpres';
import { apellidoNombre } from '../../utils/nombrePersona';

export const TIPO_SOLO_DOCUMENTACION = 'SOLO_DOCUMENTACION';
export const TIPO_REATENCION = 'REATENCION';
// Volvió de ANSES para abrir el plan de pago (15-oct-2026): a la cola, con la ficha "Apertura del plan".
export const TIPO_APERTURA_PLAN = 'APERTURA_PLAN';

const OPCION_APERTURA_PLAN = {
    valor: TIPO_APERTURA_PLAN,
    icono: 'bi-cash-coin',
    titulo: 'Volvió de ANSES por el plan de pago',
    ayuda: 'Vuelve a la cola; quien lo llame abre la ficha "Apertura del plan".',
};
const MAX_OBSERVACION = 500;

const OPCIONES_RETORNO = [
    {
        valor: TIPO_SOLO_DOCUMENTACION,
        icono: 'bi-folder-symlink',
        titulo: 'Solo entrega / retira documentación',
        ayuda: 'Trámite exprés: queda registrado y finalizado en el acto.',
    },
    {
        valor: TIPO_REATENCION,
        icono: 'bi-person-lines-fill',
        titulo: 'Requiere ser atendido nuevamente',
        ayuda: 'Vuelve a la sala de espera y aparece en la cola de atención.',
    },
];

// "2026-09-30T11:42:10" → "11:42" si fue hoy, o "el 30/09/2026 11:42" si fue otro día: el cliente
// buscado en todo el padrón puede volver por una atención de días atrás. LocalDateTime sin zona.
function cuandoFinalizo(fechaHora) {
    const texto = fechaHora ? String(fechaHora) : '';
    if (texto.length < 16) return '-';
    const hora = texto.substring(11, 16);
    const hoy = new Date();
    const hoyISO = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    if (texto.substring(0, 10) === hoyISO) return hora;
    return `el ${texto.substring(8, 10)}/${texto.substring(5, 7)}/${texto.substring(0, 4)} ${hora}`;
}

const TITULO_POR_TIPO = {
    [TIPO_SOLO_DOCUMENTACION]: 'Trámite exprés',
    [TIPO_REATENCION]: 'Volver a atender',
    [TIPO_APERTURA_PLAN]: 'Volvió de ANSES por el plan',
};

/**
 * Registra que un cliente ya atendido (turno FINALIZADO) volvió al estudio. No reabre el turno:
 * el backend crea uno nuevo enganchado al anterior (POST /recepcion/turnos/{id}/reingreso).
 *
 * Se usa desde PanelDiario ("Llegó cliente" y tarjetas de finalizados) y TurneroPage (barra
 * superior y detalle). `tipoInicial` preselecciona la opción. Con `tipoFijo` el tipo ya se eligió
 * antes de abrir ("Llegó cliente" del Panel Diario) y no se vuelve a preguntar.
 */
function ModalReingresoCliente({ show, turnoOrigen, onCerrar, onRegistrado, tipoInicial = TIPO_REATENCION, tipoFijo = false }) {
    const [tipo, setTipo] = useState(tipoInicial);
    const [observacion, setObservacion] = useState('');
    const [datosExpres, setDatosExpres] = useState(DATOS_EXPRES_VACIOS);
    const [area, setArea] = useState('');
    const [prioridad, setPrioridad] = useState(false);
    const [procesando, setProcesando] = useState(false);
    const [error, setError] = useState('');

    const idTurnoOrigen = turnoOrigen?.idTurno;
    // Un turno ANSES de plan que está "En ANSES" solo puede volver por el plan; los demás, como siempre.
    const opciones = turnoOrigen?.vuelvePorPlan ? [OPCION_APERTURA_PLAN] : OPCIONES_RETORNO;

    // Se resetea al abrir y al cambiar de turno, no en cada render: depende del id y no del objeto,
    // que el padre vuelve a crear en cada refresco de la lista.
    useEffect(() => {
        if (!show) return;
        setTipo(turnoOrigen?.vuelvePorPlan ? TIPO_APERTURA_PLAN : tipoInicial);
        setObservacion('');
        setDatosExpres(DATOS_EXPRES_VACIOS);
        setArea(turnoOrigen?.atencion || derivarAtencion(turnoOrigen?.tipoTramite, turnoOrigen?.modalidad));
        setPrioridad(false);
        setError('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, idTurnoOrigen, tipoInicial]);

    const esExpres = tipo === TIPO_SOLO_DOCUMENTACION;
    const puedeConfirmar = !procesando && !!idTurnoOrigen
        && (esExpres ? datosExpresValidos(datosExpres) : !!area);

    const cerrar = useCallback(() => {
        if (procesando) return;
        onCerrar?.();
    }, [procesando, onCerrar]);

    const confirmar = useCallback(async () => {
        if (!puedeConfirmar) return;
        setProcesando(true);
        setError('');
        try {
            const datos = esExpres
                ? {
                    tipoReingreso: tipo,
                    documentacion: datosExpres.documentacion.trim(),
                    motivoExpres: datosExpres.motivo,
                    motivoExpresDetalle: datosExpres.motivoDetalle.trim() || null,
                    observaciones: datosExpres.observaciones.trim() || null,
                    prioridad: false,
                }
                : {
                    tipoReingreso: tipo,
                    observaciones: observacion.trim() || null,
                    atencion: area,
                    prioridad,
                };
            const creado = await registrarReingreso(idTurnoOrigen, { ...datos, dni: turnoOrigen?.dni || null });
            onRegistrado?.(creado);
        } catch (e) {
            setError(e?.response?.data?.mensaje || 'No se pudo registrar el retorno del cliente.');
        } finally {
            setProcesando(false);
        }
    }, [puedeConfirmar, idTurnoOrigen, tipo, esExpres, datosExpres, observacion, area, prioridad, turnoOrigen, onRegistrado]);

    return (
        <Modal className="rec-modal" show={show} onHide={cerrar} centered scrollable size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {tipoFijo && esExpres
                        ? <i className="bi bi-lightning-charge-fill me-2"></i>
                        : <ReplayIcon className="me-2" />}
                    {tipoFijo ? TITULO_POR_TIPO[tipo] : 'Registrar retorno de cliente'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {turnoOrigen && (
                    <div className="rec-modal-resumen rounded-4">
                        <div className="fw-bold fs-5">
                            {apellidoNombre(turnoOrigen.apellido, turnoOrigen.nombre) || '-'}
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-3 mt-1 small text-secondary">
                            <span>
                                <BadgeIcon fontSize="small" className="me-1" />
                                DNI {turnoOrigen.dni || turnoOrigen.cuil || '-'}
                            </span>
                            <span>
                                <ReceiptLongIcon fontSize="small" className="me-1" />
                                {formatearTipoTramite(turnoOrigen.tipoTramite)}
                            </span>
                        </div>
                        <div className="mt-2 small">
                            <i className="bi bi-link-45deg me-1"></i>
                            Turno anterior
                            {turnoOrigen.numeroIdentificacion && <strong> #{turnoOrigen.numeroIdentificacion}</strong>}
                            {' · '}
                            <AccessTimeIcon fontSize="inherit" className="me-1" />
                            {turnoOrigen.vuelvePorPlan ? 'salió hacia ANSES por el plan de pago' : <>finalizado {cuandoFinalizo(turnoOrigen.finAtencion)}</>}
                            {turnoOrigen.operadorNombre && <> · atendió {turnoOrigen.operadorNombre}</>}
                        </div>
                    </div>
                )}

                {!tipoFijo && (
                <>
                <label className="form-label fw-semibold mt-3 mb-2">¿A qué volvió? *</label>
                <div className="rec-reingreso-opciones">
                    {opciones.map((opcion) => {
                        const activa = tipo === opcion.valor;
                        return (
                            <button
                                key={opcion.valor}
                                type="button"
                                className={`rec-reingreso-opcion rounded-4 ${activa ? 'rec-reingreso-opcion--activa' : ''}`}
                                onClick={() => setTipo(opcion.valor)}
                                disabled={procesando}
                                aria-pressed={activa}
                            >
                                <i className={`bi ${opcion.icono} rec-reingreso-opcion-icono`}></i>
                                <span>
                                    <span className="rec-reingreso-opcion-titulo d-block">{opcion.titulo}</span>
                                    <span className="rec-reingreso-opcion-ayuda d-block">{opcion.ayuda}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
                </>
                )}

                {esExpres ? (
                    <CamposTramiteExpres
                        valor={datosExpres}
                        onChange={setDatosExpres}
                        disabled={procesando}
                        idPrefijo="reingreso-expres"
                    />
                ) : (
                    <>
                        <label className="form-label fw-semibold mt-3 mb-2">¿A qué área lo derivamos? *</label>
                        <SelectorAreaAtencion valor={area} onChange={setArea} disabled={procesando} />
                        <div className="form-check form-switch mt-3">
                            <input
                                id="reingreso-prioridad"
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={prioridad}
                                onChange={(e) => setPrioridad(e.target.checked)}
                                disabled={procesando}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="reingreso-prioridad">
                                Prioridad por reingreso
                                <span className="d-block small text-secondary fw-normal">
                                    Pasa primero en la cola de espera.
                                </span>
                            </label>
                        </div>
                        <label className="form-label fw-semibold mt-3 mb-2" htmlFor="reingreso-observacion-cola">
                            Observación (opcional)
                        </label>
                        <textarea
                            id="reingreso-observacion-cola"
                            className="form-control rounded-4"
                            rows={2}
                            maxLength={MAX_OBSERVACION}
                            placeholder="Ej.: se olvidó de preguntar por el retroactivo"
                            value={observacion}
                            onChange={(e) => setObservacion(e.target.value)}
                            disabled={procesando}
                        />
                    </>
                )}

                {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <button className="rec-btn-secondary" onClick={cerrar} disabled={procesando}>Cancelar</button>
                <button className="rec-btn-primary" onClick={confirmar} disabled={!puedeConfirmar}>
                    {procesando
                        ? <span className="spinner-border spinner-border-sm me-1" />
                        : <i className={`bi ${esExpres ? 'bi-check2-all' : 'bi-arrow-right-circle'} me-1`} />}
                    {esExpres ? 'Registrar y finalizar' : 'Enviar a la cola'}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalReingresoCliente;
